/**
 * What the web app can know about the native shell it is running inside.
 *
 * TWO readers now: the build stamp, and which shell it is (`frameShell`, since
 * 2026-10-05). This module used to capture a `?shell=<capacitor origin>`
 * query parameter on first load and keep it for a "Disconnect from this server"
 * row in the main menu. Its only producer was the shell's bootstrap page, deleted
 * in 01c2ef2 (2026-08): with `server.url` set, the WebView navigates straight to
 * the origin with no query string at all, so nothing ever arrived, nothing was
 * stored, and the row silently never rendered — measured by avg-app-builder on a
 * fresh Android install, 2026-09-09 (href https://app-dev.avengents.ai/, search
 * none, stored keys {}). And the page the row navigated to no longer exists.
 * Both ends looked fine for a month; nothing renders an absence. Deleted rather
 * than revived: on a server.url shell there is nowhere to "disconnect" TO, and
 * signing out already has its own button.
 */

function inShell(): boolean {
  return 'Capacitor' in window && !!(window as unknown as { Capacitor?: unknown }).Capacitor
}

/**
 * The shell's build, as it stamps it into the User-Agent: `AvengentsShell/<sha>`,
 * `+dirty` when the tree was not clean. Bounded so a hostile UA cannot hand the
 * page an arbitrary string. The stamp reached nobody for a month (the shell wrote
 * it where Capacitor ignores it — fixed at 79c51ae); the main menu's "This app"
 * row is its reader.
 */
const UA_BUILD = /\bAvengentsShell\/([\w.+-]{1,64})\b/

export function getShellBuild(): string | null {
  if (!inShell()) return null
  try {
    return UA_BUILD.exec(navigator.userAgent)?.[1] ?? null
  } catch {
    return null
  }
}

/**
 * WHICH SHELL — for one question: may a page that is NOT this account's be framed
 * here? (Owner, 2026-10-05. The rule is artifacts/everywhere.ts `sharedViewGate`;
 * this only reports what the page can observe.)
 *
 *   none       a plain browser.
 *   mac        the Mac shell — AppKit + WKWebView, not Capacitor. Its build stamp
 *              says so: `AvengentsShell/mac-<build>`.
 *   capacitor  the iOS / Android shell. Told by Capacitor's own global, and by the
 *              shell's User-Agent tokens as a second witness: Capacitor injects its
 *              runtime into the MAIN frame only, so a document of the App running in
 *              a subframe of the same web view (a split pane) has no global and the
 *              same User-Agent.
 *
 * `frameGuard` is the capability token `AvengentsFrameGuard/<n>`: the shell saying
 * that its native bridges act only for the App's own main frame — announced the way
 * `AvengentsPasskeys/1` announces the passkey entitlement (backup/passkey.ts). The
 * shell's half of that contract is docs/NATIVE.md §16.
 *
 * READ LIVE, like the build stamp above, so nothing here can outlast a shell update.
 * And where the page cannot tell, it answers as the shell that has promised nothing:
 * a wrong "plain browser" is the one mistake here that would put someone else's page
 * within reach of an unguarded bridge.
 */
export type FrameShell = { kind: 'none' | 'mac' | 'capacitor'; frameGuard: boolean }

const UA_OURS = /\bAvengents(?:Shell|Id)\//
const UA_MAC = /\bAvengentsShell\/mac-/
const UA_FRAME_GUARD = /\bAvengentsFrameGuard\/\d+/

export function frameShell(): FrameShell {
  try {
    const ua = navigator.userAgent || ''
    const cap = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor
    const native = cap?.isNativePlatform?.() === true
    const kind = native ? 'capacitor' : UA_MAC.test(ua) ? 'mac' : UA_OURS.test(ua) ? 'capacitor' : 'none'
    return { kind, frameGuard: UA_FRAME_GUARD.test(ua) }
  } catch {
    return { kind: 'capacitor', frameGuard: false }
  }
}
